import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { showTemplates, syncTemplates, getCompany, deleteTemplate, getTemplateInsights } from '../../../helpers';
import './TemplateListPage.css';
import ViewTemplate from './ViewTemplate';
import { Eye, Trash, ArrowRepeat, BarChartLine, Check2, Check2All, Reply, Send, XCircle, People, ListUl } from 'react-bootstrap-icons';

interface TemplateInsight {
    template: { id: number; name: string; category: string; language: string; status: string };
    rate: number;
    billable: number;
    amount: number;
    metrics: {
        reach: number; sent: number; delivered: number; read: number;
        replied: number; sending: number; failed: number; total: number;
    };
}

// Published WhatsApp conversation pricing (INR per message) shown for reference.
const CATEGORY_PRICING = [
    { key: 'marketing', label: 'Marketing', desc: 'Offers, promotions, campaigns', rate: 0.8631 },
    { key: 'utility', label: 'Utility', desc: 'Appointment confirmation, bill, payment reminder', rate: 0.1150 },
    { key: 'authentication', label: 'Authentication', desc: 'OTP, login verification', rate: 0.1150 },
];

interface Template {
    template_id: number;
    template_name: string;
    category: string;
    language: string;
    wa_approval_status: string;
    status: string;
    created_at: string;
    title?: string;
    title_text?: string;
    title_type?: string;
    title_file?: string;
    body: string;
    footer?: string;
    buttons?: any[];
    header_variables?: string[];
    variables?: string[];
}

const ITEMS_PER_PAGE = 6;
interface Props {
    alertMsg?: string | null;
    alertVariant?: 'success' | 'danger' | 'primary';
}

const ListPage: React.FC<Props> = ({ alertMsg, alertVariant = 'success' }) => {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [syncLoading, setSyncLoading] = useState(false);
    const [companyName, setCompanyName] = useState('Loading...');
    const [profile, setProfile] = useState('logo');
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [activeCategory, setActiveCategory] = useState('All');
    const [categoryCounts, setCategoryCounts] = useState<{ [k: string]: number }>({});
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [company, setCompanyid] = useState('0');

    // Template insights modal
    const [showInsightModal, setShowInsightModal] = useState(false);
    const [insightLoading, setInsightLoading] = useState(false);
    const [insightError, setInsightError] = useState<string | null>(null);
    const [insight, setInsight] = useState<TemplateInsight | null>(null);

    const openInsights = async (template: Template) => {
        setShowInsightModal(true);
        setInsight(null);
        setInsightError(null);
        setInsightLoading(true);
        try {
            const res = await getTemplateInsights(template.template_id);
            setInsight(res.data);
        } catch (err) {
            console.error('Insights error:', err);
            setInsightError('Could not load insights for this template.');
        } finally {
            setInsightLoading(false);
        }
    };

    const companyId =
        JSON.parse(sessionStorage.getItem('shreyu_user') || '{}')?.user?.company_id;

    const normalizeTemplates = (list: Template[]) =>
        list.map(t => ({ ...t, buttons: Array.isArray(t.buttons) ? t.buttons : [] }));

    const loadTemplates = async () => {
        const res = await showTemplates();
        const data = normalizeTemplates(res.data.templates);

        setTemplates(data);
        setFilteredTemplates(data);

        const counts = { All: data.length, Marketing: 0, Utility: 0, Authentication: 0 };

        data.forEach(t => {
            const c = t.category?.toLowerCase();
            if (c === 'marketing') counts.Marketing++;
            if (c === 'utility') counts.Utility++;
            if (c === 'authentication') counts.Authentication++;
        });

        setCategoryCounts(counts);
        setLoading(false);
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await getCompany(companyId);
                setCompanyName(res.data.company_name);
                setCompanyid(companyId);
                setProfile(res.data.logo || 'logo');
            } catch { }
        })();
    }, []);

    const handleSync = async () => {
        setSyncLoading(true);
        try {
            await syncTemplates();
            await loadTemplates();
        } finally {
            setSyncLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;

        try {
            const res = await deleteTemplate(id);
            if (res.data.success) {
                setTemplates(prev => prev.filter(t => t.template_id !== id));
                setFilteredTemplates(prev => prev.filter(t => t.template_id !== id));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const filterByCategory = (cat: string) => {
        setActiveCategory(cat);
        setCurrentPage(1);

        if (cat === 'All') setFilteredTemplates(templates);
        else
            setFilteredTemplates(
                templates.filter(t => t.category?.toLowerCase() === cat.toLowerCase())
            );
    };

    const getStatusBadge = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return <Badge bg="success">Approved</Badge>;
            case 'pending':
                return <Badge bg="warning">Pending</Badge>;
            case 'rejected':
                return <Badge bg="danger">Rejected</Badge>;
            default:
                return <Badge bg="secondary">{status || 'Unknown'}</Badge>;
        }
    };

    const escapeRegExp = (string: string) =>
        string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const renderPreviewText = (text: string, variables: string[] = []) => {
        let result = text || '';

        variables.forEach((v, i) => {
            const regex = new RegExp(escapeRegExp(v), 'g');
            result = result.replace(regex, `[Variable ${i + 1}]`);
        });

        result = result
            .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
            .replace(/_(.*?)_/g, '<em>$1</em>')
            .replace(/~(.*?)~/g, '<span style="text-decoration: line-through">$1</span>')
            .replace(/\\n/g, '<br/>')
            .replace(/\n/g, '<br/>');

        return result;
    };

    const renderButtonPreview = (button: any, index: number) => {
        if (!button) return null;
        const { types, text } = button;

        return (
            <div key={index} className="wa-button-wrapper">
                {(types === 'PHONE_NUMBER' || types === 'call_to_action') && (
                    <b style={{ color: '#1E90FF' }}>{text || 'Call'}</b>
                )}
                {types === 'URL' && <b style={{ color: '#1E90FF' }}>{text || 'Visit Site'}</b>}
                {types === 'QUICK_REPLY' && (
                    <b style={{ color: '#1E90FF' }}>{text || 'Quick Reply'}</b>
                )}
            </div>
        );
    };

    const renderHeaderPreview = (template: Template) => {
        if (!template.title_type || template.title_type === 'none') return null;

        if (template.title_type.toLowerCase() === 'text') {
            return (
                <div
                    dangerouslySetInnerHTML={{
                        __html: renderPreviewText(template.title || '', template.header_variables || [])
                    }}
                />
            );
        }

        if (template.title_type.toLowerCase() === 'image') {
            return (
                <img
                    src={`https://api.skynyx.ai/file_storage/${companyId}/template/${template.title_file}`}
                    alt=""
                    style={{ width: '50%', borderRadius: 6 }}
                />
            );
        }

        return null;
    };

    const searchedTemplates = filteredTemplates.filter(t =>
        t.template_name?.toLowerCase().includes(searchText.toLowerCase())
    );

    const totalPages = Math.ceil(searchedTemplates.length / ITEMS_PER_PAGE);

    const paginatedTemplates = searchedTemplates.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    if (loading) return <div className="text-center py-5">Loading templates...</div>;

    return (
        // <>
        <Row >
            {alertMsg && (
                <Alert variant={alertVariant} dismissible>
                    {alertMsg}
                </Alert>
            )}

            <Col md={2}>
                <Card>
                    <Card.Body>
                        <h6>Groups</h6>

                        {['All', 'Marketing', 'Utility', 'Authentication'].map((cat) => (
                            <div
                                key={cat}
                                className={`group-item ${activeCategory === cat ? 'active' : ''}`}
                                onClick={() => filterByCategory(cat)}
                            >
                                {cat} ({categoryCounts[cat] || 0})
                            </div>
                        ))}


                    </Card.Body>
                </Card>
            </Col>

            <Col md={10}>
                <Card>
                    <Card.Body>
                        <Row>
                            <Col>
                                <div className="template-toolbar">
                                    <Form.Control
                                        size="sm"
                                        placeholder="Search template..."
                                        value={searchText}
                                        onChange={e => {
                                            setSearchText(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            </Col>
                            <Col>
                                <Button size="sm" onClick={handleSync} disabled={syncLoading}>
                                    <ArrowRepeat className={syncLoading ? 'spin me-1' : 'me-1'} />
                                    Sync
                                </Button>
                            </Col>
                        </Row>

                        <Row className="mt-3 g-3">
                            {paginatedTemplates.map(template => (
                                <Col key={template.template_id} lg={6} xl={4}>
                                    <Card className=" template-card">
                                        <Card.Header className=" text-white d-flex justify-content-between align-items-center py-1 px-2" style={{
                                background: 'linear-gradient(135deg, #0eb7eb, #167491)',
                               
                            }}>
                                            <div style={{ fontSize: '12px', lineHeight: '1.2' }}>
                                                <div style={{ fontWeight: 600 }}>{template.template_name}</div>
                                                <div className="text-light">
                                                    {template.category} • {template.language}
                                                </div>
                                            </div>

                                            <div style={{ fontSize: '11px' }}>
                                                {getStatusBadge(template.wa_approval_status)}
                                            </div>
                                        </Card.Header>

                                        <Card.Body>
                                            <div className="template-preview-container">
                                                <div className="template-preview fixed-preview">
                                                    <div className="template-header-preview">
                                                        <div className="template-profile-pic">
                                                            <img
                                                                src={`https://api.skynyx.ai/storage/app/public/${profile}`}
                                                                className="me-2 rounded-circle"
                                                                height="40"
                                                                alt=""
                                                                style={{ backgroundColor: "white" }}
                                                            />
                                                        </div>
                                                        <div className="template-contact">
                                                            <div className="template-contact-name text-white">{companyName}</div>
                                                            <div className="template-contact-status">online</div>
                                                        </div>
                                                    </div>
                                                    <div className="template-message-preview">
                                                        {renderHeaderPreview(template)}
                                                        <div className="template-body-preview pl-2" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}  >
                                                            <div dangerouslySetInnerHTML={{
                                                                __html: renderPreviewText(template.body, template.variables || [])
                                                            }} />
                                                        </div>
                                                        {template.footer && (
                                                            <div className="template-footer-preview" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}>
                                                                {template.footer}
                                                            </div>
                                                        )}
                                                        {/* {console.log(template.buttons)} */}
                                                        {template.buttons && template.buttons.length > 0 && (
                                                            <div className="template-buttons-preview" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}>
                                                                {template.buttons.map((button, index) =>
                                                                    renderButtonPreview(button, index)
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Card.Body>

                                        <Card.Footer className="text-center">
                                            <Row>
                                                <Col>
                                                    <small>
                                                        {new Date(template.created_at).toLocaleDateString()}
                                                    </small>
                                                </Col>
                                                <Col>
                                                    <div className=" d-flex justify-content-center gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="info"
                                                            title="Preview"
                                                            onClick={() => {
                                                                setSelectedTemplateId(template.template_id);
                                                                setShowTemplateModal(true);
                                                            }}
                                                        >
                                                            <Eye />
                                                        </Button>

                                                        <Button
                                                            size="sm"
                                                            variant="success"
                                                            title="Insights"
                                                            onClick={() => openInsights(template)}
                                                        >
                                                            <BarChartLine />
                                                        </Button>

                                                        <Button
                                                            size="sm"
                                                            variant="danger"
                                                            title="Delete"
                                                            onClick={() => handleDelete(template.template_id)}
                                                        >
                                                            <Trash />
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Footer>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
                                <Button size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                                    Prev
                                </Button>
                                <span>
                                    Page {currentPage} / {totalPages}
                                </span>
                                <Button size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                                    Next
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>


            <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="xl">
                <Modal.Header closeButton />
                <Modal.Body>
                    <ViewTemplate templateId={selectedTemplateId} />
                </Modal.Body>
            </Modal>

            {/* TEMPLATE INSIGHTS MODAL */}
            <Modal show={showInsightModal} onHide={() => setShowInsightModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: 18 }}>
                        <BarChartLine /> Template Insights
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {insightLoading && (
                        <div className="text-center py-5 text-muted">Loading insights…</div>
                    )}

                    {!insightLoading && insightError && (
                        <Alert variant="danger" className="mb-0">{insightError}</Alert>
                    )}

                    {!insightLoading && !insightError && insight && (
                        <>
                            {/* Heading */}
                            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                                <div>
                                    <div style={{ fontSize: 17, fontWeight: 700 }}>{insight.template.name}</div>
                                    <div className="text-muted" style={{ fontSize: 13 }}>
                                        {insight.template.category} • {insight.template.language}
                                    </div>
                                </div>
                                {getStatusBadge(insight.template.status)}
                            </div>

                            {/* Metric tiles */}
                            <Row className="g-2 mb-3">
                                {[
                                    { label: 'Reach', value: insight.metrics.reach, icon: <People size={16} />, bg: '#eef2ff', color: '#5b6ef5' },
                                    { label: 'Sent', value: insight.metrics.sent, icon: <Check2 size={16} />, bg: '#e9f7ef', color: '#2e9e5b' },
                                    { label: 'Delivered', value: insight.metrics.delivered, icon: <Check2All size={16} />, bg: '#e7f1ff', color: '#2f6fed' },
                                    { label: 'Read', value: insight.metrics.read, icon: <Eye size={16} />, bg: '#eafaf1', color: '#12b76a' },
                                    { label: 'Replied', value: insight.metrics.replied, icon: <Reply size={16} />, bg: '#e8f8f5', color: '#0ca789' },
                                    { label: 'Sending', value: insight.metrics.sending, icon: <Send size={16} />, bg: '#fff4e5', color: '#f79009' },
                                    { label: 'Failed', value: insight.metrics.failed, icon: <XCircle size={16} />, bg: '#fdecec', color: '#e5484d' },
                                    { label: 'Total', value: insight.metrics.total, icon: <ListUl size={16} />, bg: '#f1f3f7', color: '#5b6472' },
                                ].map((s) => (
                                    <Col key={s.label} xs={6} sm={3}>
                                        <div
                                            className="d-flex align-items-center justify-content-between h-100"
                                            style={{ padding: '12px 14px', background: '#f8f9fb', borderRadius: 12, border: '1px solid #eef0f4' }}
                                        >
                                            <div>
                                                <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, color: '#1b1f27' }}>
                                                    {s.value.toLocaleString()}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: 12 }}>{s.label}</div>
                                            </div>
                                            <span
                                                className="d-flex align-items-center justify-content-center"
                                                style={{ width: 30, height: 30, borderRadius: '50%', background: s.bg, color: s.color, flexShrink: 0 }}
                                            >
                                                {s.icon}
                                            </span>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {/* Estimated cost */}
                            <div
                                className="d-flex flex-wrap align-items-center justify-content-between mb-3"
                                style={{ padding: '14px 18px', background: 'linear-gradient(135deg, #0eb7eb, #167491)', borderRadius: 12, color: '#fff' }}
                            >
                                <div>
                                    <div style={{ fontSize: 13, opacity: 0.9 }}>Estimated cost</div>
                                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                                        {insight.billable.toLocaleString()} billable msgs × ₹{insight.rate.toFixed(4)}
                                    </div>
                                </div>
                                <div style={{ fontSize: 26, fontWeight: 700 }}>
                                    ₹{insight.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                            </div>

                            {/* Pricing reference */}
                            <div style={{ fontSize: 13, fontWeight: 600 }} className="mb-2">Pricing per message (INR)</div>
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr className="text-muted" style={{ fontSize: 12 }}>
                                            <th>Category</th>
                                            <th>Use case</th>
                                            <th className="text-end">Rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {CATEGORY_PRICING.map((p) => {
                                            const active = p.key === (insight.template.category || '').toLowerCase();
                                            return (
                                                <tr key={p.key} style={active ? { background: '#eafaf1', fontWeight: 600 } : undefined}>
                                                    <td>{p.label}</td>
                                                    <td className="text-muted" style={{ fontSize: 12 }}>{p.desc}</td>
                                                    <td className="text-end">₹{p.rate.toFixed(4)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowInsightModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </Row>

        // </>
    );
};

export default ListPage;
